import type {SafeHTMLString} from '@github-ui/safe-html'
import {SafeHTMLText} from '@github-ui/safe-html'
import {Link} from '@primer/react'
import {useMemo} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {VALUES} from '../constants/values'
import type {LabelData$key} from './__generated__/LabelData.graphql'
import {LabelToken} from '@github-ui/label-token'
import {VisuallyHidden} from '@github-ui/visually-hidden'
import {Tooltip} from '@primer/react/experimental'

type LabelProps = {
  queryRef: LabelData$key
  timelineEventBaseUrl: string
}

export function Label({queryRef, timelineEventBaseUrl}: LabelProps): JSX.Element {
  const {
    nameHTML,
    name,
    color,
    id,
    description,
    repository: {
      name: repoName,
      owner: {login: ownerLogin},
    },
  } = useFragment(
    graphql`
      fragment LabelData on Label {
        nameHTML
        name
        color
        id
        description
        repository {
          name
          owner {
            login
          }
        }
      }
    `,
    queryRef,
  )

  const url = useMemo(() => {
    return `${timelineEventBaseUrl}?q=${encodeURIComponent(VALUES.labelQuery(ownerLogin, repoName, name))}`
  }, [timelineEventBaseUrl, ownerLogin, repoName, name])

  return (
    <Tooltip
      text={description ?? ''}
      type="description"
      aria-label={description ?? ''}
      sx={{position: 'absolute', visibility: description ? 'visible' : 'hidden'}}
    >
      <Link href={url} sx={{color: 'fg.default'}} aria-describedby={`${id}-tooltip`} muted>
        <LabelToken
          text={<SafeHTMLText html={nameHTML as SafeHTMLString} />}
          fillColor={`#${color}`}
          key={id}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '20ch',
            cursor: 'pointer',
          }}
        />
        <VisuallyHidden id={`${id}-tooltip`}>{description ?? ''}</VisuallyHidden>
      </Link>
    </Tooltip>
  )
}

try{ Label.displayName ||= 'Label' } catch {}